import React, { createContext, useState, useEffect, useCallback } from 'react';

// Function to check if a JWT token is expired
const checkIfTokenIsExpired = (token) => {
  try {
    // Split the token and get the payload part
    const payload = token.split('.')[1];
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );
    // Decode the base64 payload
    const decodedPayload = JSON.parse(atob(paddedPayload));
    // Check if the token is expired
    return decodedPayload.exp * 1000 < Date.now();
  } catch (error) {
    console.error('Error decoding token:', error);
    // If we can't decode the token, assume it's invalid
    return true;
  }
};

const decodeTokenPayload = (token) => {
  try {
    const payload = token.split('.')[1];
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );
    return JSON.parse(atob(paddedPayload));
  } catch (error) {
    console.error('Error decoding token payload:', error);
    return null;
  }
};

// Create a Context for the user
export const UserContext = createContext();

// Create a provider component
export const UserProvider = ({ children }) => {
  const [userId, setUserId] = useState(() => {
    // Retrieve userId from local storage if available
    const storedUserId = localStorage.getItem('userId');
    return storedUserId ? parseInt(storedUserId, 10) : null;
  });

  const [userEmail, setUserEmail] = useState(() => {
    // Retrieve userEmail from local storage if available
    return localStorage.getItem('userEmail') || null;
  });

  // True while the account still holds the emailed temporary password. This is
  // a routing convenience only, the backend independently refuses every
  // authenticated route in this state, so clearing the flag here buys nothing.
  const [mustChangePassword, setMustChangePasswordState] = useState(() => {
    return localStorage.getItem('must_change_password') === 'true';
  });

  const [isPlatformAdmin, setIsPlatformAdmin] = useState(() => {
    const storedIsPlatformAdmin = localStorage.getItem('isPlatformAdmin');
    if (storedIsPlatformAdmin !== null) {
      return storedIsPlatformAdmin === 'true';
    }

    const legacyIsAdmin = localStorage.getItem('isAdmin');
    return legacyIsAdmin === 'true';
  });

  const [organizationRole, setOrganizationRole] = useState(() => {
    return localStorage.getItem('organizationRole') || null;
  });

  const [organizationId, setOrganizationId] = useState(() => {
    const storedOrganizationId = localStorage.getItem('organizationId');
    if (!storedOrganizationId) return null;
    const parsedOrganizationId = parseInt(storedOrganizationId, 10);
    return Number.isNaN(parsedOrganizationId) ? null : parsedOrganizationId;
  });

  const [userAddOns, setUserAddOns] = useState([]);

  // Load user add-ons
  const loadUserAddOns = useCallback(async () => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
    } catch (error) {
      console.error('Error loading user add-ons:', error);
      setUserAddOns([]);
    }
  }, [userId]);

  // Load user add-ons when userId changes
  useEffect(() => {
    loadUserAddOns();
  }, [loadUserAddOns]);

  // Example: Set the user ID, email, and admin status after login
  // const login = (id, email = null, adminStatus = false) => {
  //   const parsedId = parseInt(id, 10);
  //   setUserId(parsedId);
  //   setUserEmail(email);
  //   setIsAdmin(adminStatus);
  //   localStorage.setItem('userId', parsedId.toString());
  //   if (email) localStorage.setItem('userEmail', email);
  //   localStorage.setItem('isAdmin', adminStatus.toString());
  // };
const setMustChangePassword = (value) => {
    setMustChangePasswordState(value);
    if (value) {
      localStorage.setItem('must_change_password', 'true');
    } else {
      localStorage.removeItem('must_change_password');
    }
  };

const login = (id, email = null, adminStatus = false, roleData = {}) => {
    console.log('=== LOGIN FUNCTION CALLED ===');
    console.log('Input parameters:', { id, email, adminStatus, roleData });
    
    const parsedId = parseInt(id, 10);
    console.log('Parsed ID:', parsedId);

    const resolvedPlatformAdmin = roleData.isPlatformAdmin ?? adminStatus;
    const resolvedOrganizationRole = roleData.organizationRole ?? null;
    const resolvedOrganizationId = roleData.organizationId ?? null;
    
    setUserId(parsedId);
    setUserEmail(email);
    setIsPlatformAdmin(resolvedPlatformAdmin);
    setOrganizationRole(resolvedOrganizationRole);
    setMustChangePassword(roleData.mustChangePassword === true);
    const parsedOrganizationId =
      resolvedOrganizationId !== null && resolvedOrganizationId !== undefined
        ? parseInt(resolvedOrganizationId, 10)
        : null;
    setOrganizationId(Number.isNaN(parsedOrganizationId) ? null : parsedOrganizationId);
    
    // Set localStorage
    localStorage.setItem('userId', parsedId.toString());
    if (email) localStorage.setItem('userEmail', email);
    localStorage.setItem('isAdmin', resolvedPlatformAdmin.toString());
    localStorage.setItem('isPlatformAdmin', resolvedPlatformAdmin.toString());
    if (resolvedOrganizationRole) {
      localStorage.setItem('organizationRole', resolvedOrganizationRole);
    } else {
      localStorage.removeItem('organizationRole');
    }
    if (!Number.isNaN(parsedOrganizationId) && parsedOrganizationId !== null) {
      localStorage.setItem('organizationId', String(parsedOrganizationId));
    } else {
      localStorage.removeItem('organizationId');
    }
    
    console.log('State set, localStorage updated');
    console.log('Final localStorage state:', {
        userId: localStorage.getItem('userId'),
        userEmail: localStorage.getItem('userEmail'),
        isAdmin: localStorage.getItem('isAdmin'),
        isPlatformAdmin: localStorage.getItem('isPlatformAdmin'),
        organizationRole: localStorage.getItem('organizationRole'),
        organizationId: localStorage.getItem('organizationId'),
        access_token: localStorage.getItem('access_token') ? 'EXISTS' : 'MISSING'
    });
};

  const logout = () => {
    console.log('Logout process started...');

    // Step 1: Immediately clear the main application's data from the client.
    console.log('Clearing main application client-side user data...');
    setUserId(null);
    setUserEmail(null);
    setIsPlatformAdmin(false);
    setOrganizationRole(null);
    setOrganizationId(null);
    setMustChangePassword(false);
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('isPlatformAdmin');
    localStorage.removeItem('organizationRole');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('access_token');
    localStorage.removeItem('ap_token');
    localStorage.removeItem('projectId');
    localStorage.removeItem('token');
    // Step 2: Navigate to the proxy's GET logout endpoint.
    // const baseUrl = process.env.REACT_APP_API_AP_URL;
    // if (!baseUrl) {
    //   console.error("REACT_APP_API_AP_URL is not defined. Cannot call proxy logout.");
    //   // Fallback: If proxy is not configured, just redirect locally.
    //   window.location.href = '/login';
    //   return;
    // }
    
    // const logoutUrl = `${baseUrl.replace(/\/+$/, '')}/logout`;

    // console.log(`Navigating to proxy for final logout: ${logoutUrl}`);
    // // The browser will make a GET request and process the HTML response from the proxy.
    // window.location.href = logoutUrl;
  };

  // Validate token on initialization - only run once at startup
  useEffect(() => {
    const validateToken = () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          const isExpired = checkIfTokenIsExpired(token);
          if (isExpired) {
            console.log('Token has expired, logging out');
            // Clear localStorage directly instead of using logout function
            localStorage.removeItem('userId');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('isAdmin');
            localStorage.removeItem('isPlatformAdmin');
            localStorage.removeItem('organizationRole');
            localStorage.removeItem('organizationId');
            localStorage.removeItem('access_token');
            localStorage.removeItem('ap_token');
            localStorage.removeItem('projectId');
            localStorage.removeItem('token');
            // Update state
            setUserId(null);
            setUserEmail(null);
            setIsPlatformAdmin(false);
            setOrganizationRole(null);
            setOrganizationId(null);
          } else {
            console.log('Token is valid');
            const decodedPayload = decodeTokenPayload(token);
            if (decodedPayload) {
              if (localStorage.getItem('isPlatformAdmin') === null) {
                const tokenPlatformAdmin = decodedPayload.is_admin === true;
                setIsPlatformAdmin(tokenPlatformAdmin);
                localStorage.setItem('isAdmin', tokenPlatformAdmin.toString());
                localStorage.setItem('isPlatformAdmin', tokenPlatformAdmin.toString());
              }

              if (localStorage.getItem('organizationRole') === null && decodedPayload.organization_role) {
                setOrganizationRole(decodedPayload.organization_role);
                localStorage.setItem('organizationRole', decodedPayload.organization_role);
              }

              if (localStorage.getItem('organizationId') === null && decodedPayload.organization_id != null) {
                const tokenOrganizationId = parseInt(decodedPayload.organization_id, 10);
                if (!Number.isNaN(tokenOrganizationId)) {
                  setOrganizationId(tokenOrganizationId);
                  localStorage.setItem('organizationId', String(tokenOrganizationId));
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error validating token:', error);
      }
    };
    
    validateToken();
    // No dependencies - run only once at initialization
  }, []);

  // Update local storage whenever user state changes
  useEffect(() => {
    if (userId) {
      localStorage.setItem('userId', userId.toString());
      if (userEmail) localStorage.setItem('userEmail', userEmail);
      localStorage.setItem('isAdmin', isPlatformAdmin.toString());
      localStorage.setItem('isPlatformAdmin', isPlatformAdmin.toString());
      if (organizationRole) {
        localStorage.setItem('organizationRole', organizationRole);
      } else {
        localStorage.removeItem('organizationRole');
      }
      if (organizationId !== null && organizationId !== undefined) {
        localStorage.setItem('organizationId', organizationId.toString());
      } else {
        localStorage.removeItem('organizationId');
      }
    } else {
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('isPlatformAdmin');
      localStorage.removeItem('organizationRole');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
      localStorage.removeItem('ap_token');
      localStorage.removeItem('projectId');

    }
  }, [userId, userEmail, isPlatformAdmin, organizationRole, organizationId]);

  const isOrganizationAdmin = organizationRole === 'ADMIN';
  const isOrganizationMember = organizationRole === 'MEMBER';
  const canAccessAdminPanel = isPlatformAdmin || isOrganizationAdmin;
  const isRestrictedOrganizationMember = isOrganizationMember && !isPlatformAdmin;

  return (
    <UserContext.Provider value={{ 
      userId, 
      userEmail, 
      isAdmin: isPlatformAdmin,
      isPlatformAdmin,
      organizationRole,
      organizationId,
      isOrganizationAdmin,
      isOrganizationMember,
      canAccessAdminPanel,
      isRestrictedOrganizationMember,
      mustChangePassword,
      setMustChangePassword,
      userAddOns,
      setUserId,
      setUserEmail,
      login,
      logout,
      loadUserAddOns
    }}>
      {children}
    </UserContext.Provider>
  );
};
